/* *
 * =============================================================================
 * AscendDraft Credit System
 * =============================================================================
 * 
 * Real-time credit system integrated with chat app backend.
 * Provides credit balance management, transaction tracking, and discount reporting.
 * 
 * FEATURES:
 * - Real-time credit balance sync with chat app
 * - Credit purchase and usage tracking
 * - Auto-apply credits at checkout
 * - Cross-service discount reporting to admin/finance
 * - Transaction history with categories
 * 
 * INTEGRATION:
 * - Syncs with identity.js for user state
 * - Reports to unified-basket.js for checkout
 * - Pushes discounts/transactions to backend for finance tracking
 * 
 * @version 1.0.0 */
'use strict';

// ==========================================================================
// CONSTANTS
// ==========================================================================

const CREDIT_STORAGE_KEY = 'ascend_credits';
  const TRANSACTIONS_KEY = 'ascend_credit_transactions';
  const PENDING_REPORTS_KEY = 'ascend_pending_reports';
  const SYNC_INTERVAL_MS = 15000; // 15 seconds
  const MAX_PENDING_REPORTS = 50;
  
  // API Endpoints
  const API_BASE = window.ASCEND_CHAT_API || 'https://chat-api.ascenddraft.com/api';
  const FINANCE_API = window.ASCEND_FINANCE_API || 'https://api.ascenddraft.com/api/finance';
  
  // Credit conversion rate
  const CREDITS_PER_POUND = 100; // 100 credits = £1
  
  // ==========================================================================
  // STATE
  // ==========================================================================
  
  let credits = {
    balance: 0,
    pendingCredits: 0, // Credits waiting for confirmation
    lifetimeEarned: 0,
    lifetimeSpent: 0,
    lastSync: null,
    userId: null
  };
  
  let transactions = [];
  let pendingReports = [];
  let syncInterval = null;
  let initialized = false;
  
  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  function init() {
    if (initialized) return Promise.resolve(credits);
    
    // Load from local storage
    loadFromStorage();
    
    // Listen for identity changes
    if (window.AscendIdentity) {
      document.addEventListener('identityChanged', handleIdentityChange);
    }
    
    // Start sync if user is authenticated
    const identity = window.AscendIdentity?.getCurrentIdentity();
    if (identity && identity.isAuthenticated) {
      credits.userId = identity.id;
      startSync();
    }
    
    // Process any pending reports
    processPendingReports();
    
    initialized = true;
    console.log('[CreditSystem] Initialized with balance:', credits.balance);
    
    return Promise.resolve(credits);
  }
  
  // ==========================================================================
  // STORAGE OPERATIONS
  // ==========================================================================
  
  function loadFromStorage() {
    try {
      const stored = localStorage.getItem(CREDIT_STORAGE_KEY);
      if (stored) {
        credits = { ...credits, ...JSON.parse(stored) };
      }
      
      const storedTxns = localStorage.getItem(TRANSACTIONS_KEY);
      if (storedTxns) {
        transactions = JSON.parse(storedTxns);
      }
      
      const storedReports = localStorage.getItem(PENDING_REPORTS_KEY);
      if (storedReports) {
        pendingReports = JSON.parse(storedReports);
      }
    } catch (e) {
      console.warn('[CreditSystem] Error loading from storage:', e);
    }
  }
  
  function saveToStorage() {
    try {
      localStorage.setItem(CREDIT_STORAGE_KEY, JSON.stringify(credits));
      localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(transactions.slice(-100))); // Keep last 100
      localStorage.setItem(PENDING_REPORTS_KEY, JSON.stringify(pendingReports.slice(-MAX_PENDING_REPORTS)));
    } catch (e) {
      console.warn('[CreditSystem] Error saving to storage:', e);
    }
  }
  
  // ==========================================================================
  // CREDIT OPERATIONS
  // ==========================================================================
  
  /* *
   * Get current credit balance */
  function getBalance() {
    return {
      available: credits.balance,
      pending: credits.pendingCredits,
      total: credits.balance + credits.pendingCredits,
      lifetimeEarned: credits.lifetimeEarned,
      lifetimeSpent: credits.lifetimeSpent,
      inPounds: credits.balance / CREDITS_PER_POUND
    };
  }
  
  /* *
   * Add credits to balance */
  function addCredits(amount, reason, metadata = {}) {
    if (amount <= 0) return false;
    
    const transaction = createTransaction('credit', amount, reason, metadata);
    
    credits.balance += amount;
    credits.lifetimeEarned += amount;
    transactions.push(transaction);
    
    saveToStorage();
    emitCreditChange('credit', amount, credits.balance);
    
    // Report to backend
    reportTransaction(transaction);
    
    return true;
  }
  
  /* *
   * Use credits from balance */
  function useCredits(amount, reason, metadata = {}) {
    if (amount <= 0 || amount > credits.balance) {
      return { success: false, error: 'Insufficient credits' };
    }
    
    const transaction = createTransaction('debit', amount, reason, metadata);
    
    credits.balance -= amount;
    credits.lifetimeSpent += amount;
    transactions.push(transaction);
    
    saveToStorage();
    emitCreditChange('debit', amount, credits.balance);
    
    // Report to backend
    reportTransaction(transaction);
    
    return { success: true, remaining: credits.balance };
  }
  
  /* *
   * Convert pound amount to credits */
  function poundToCredits(pounds) {
    return Math.round(pounds * CREDITS_PER_POUND);
  }

  /* *
   * Convert credits to pound amount */
  function creditsToPound(creditAmount) {
    return creditAmount / CREDITS_PER_POUND;
  }
  
  /* *
   * Create a transaction record */
  function createTransaction(type, amount, reason, metadata = {}) {
    const identity = window.AscendIdentity?.getCurrentIdentity();
    
    return {
      id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: type, // 'credit' or 'debit'
      amount: amount,
      reason: reason,
      category: metadata.category || 'general',
      serviceType: metadata.serviceType || null,
      orderId: metadata.orderId || null,
      discountType: metadata.discountType || null,
      discountAmount: metadata.discountAmount || 0,
      userId: identity?.id || credits.userId,
      timestamp: new Date().toISOString(),
      platform: 'website',
      metadata: metadata
    };
  }
  
  // ==========================================================================
  // DISCOUNT REPORTING
  // ==========================================================================
  
  /* *
   * Report a discount applied to admin/finance
   * Called when checkout completes with discounts */
  function reportDiscount(discountData) {
    const identity = window.AscendIdentity?.getCurrentIdentity();
    
    const report = {
      id: `disc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'discount',
      userId: identity?.id || credits.userId,
      userEmail: identity?.email || null,
      userName: identity?.fullName || 'Guest',
      discountType: discountData.type, // 'same-category', 'cross-service', 'promotional', 'credit'
      discountPercent: discountData.percent || 0,
      discountAmount: discountData.amount || 0,
      originalTotal: discountData.originalTotal || 0,
      finalTotal: discountData.finalTotal || 0,
      orderItems: discountData.items || [],
      categories: discountData.categories || [],
      serviceSummary: discountData.serviceSummary || '',
      timestamp: new Date().toISOString(),
      platform: 'website',
      status: 'pending',
      metadata: discountData.metadata || {}
    };
    
    // Queue for backend push
    queueReport(report);
    
    // Sync to chat app if cross-service discount
    if (discountData.type === 'cross-service') {
      syncDiscountToChatApp(report);
    }
    
    console.log('[CreditSystem] Discount reported:', report.discountType, '$' + report.discountAmount);
    
    return report;
  }
  
  /* *
   * Report a completed order with all financial details */
  function reportOrder(orderData) {
    const identity = window.AscendIdentity?.getCurrentIdentity();
    
    const report = {
      id: `ord_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'order',
      userId: identity?.id || credits.userId,
      userEmail: identity?.email || null,
      userName: identity?.fullName || 'Guest',
      orderId: orderData.orderId,
      subtotal: orderData.subtotal || 0,
      discounts: {
        sameCategoryDiscount: orderData.sameCategoryDiscount || 0,
        crossCategoryDiscount: orderData.crossCategoryDiscount || 0,
        promotionalDiscount: orderData.promotionalDiscount || 0,
        creditDiscount: orderData.creditDiscount || 0,
        totalDiscount: orderData.totalDiscount || 0
      },
      rushFee: orderData.rushFee || 0,
      rushLevel: orderData.rushLevel || 'standard',
      creditsUsed: orderData.creditsUsed || 0,
      creditsEarned: orderData.creditsEarned || 0,
      total: orderData.total || 0,
      paymentMethod: orderData.paymentMethod || 'unknown',
      items: orderData.items || [],
      categories: orderData.categories || [],
      timestamp: new Date().toISOString(),
      platform: 'website',
      status: 'pending',
      deliveryEstimate: orderData.deliveryEstimate || null
    };
    
    // Queue for backend push
    queueReport(report);
    
    console.log('[CreditSystem] Order reported:', report.orderId, '$' + report.total);
    
    return report;
  }
  
  /* *
   * Queue a report for backend submission */
  function queueReport(report) {
    pendingReports.push(report);
    
    // Trim if too many pending
    if (pendingReports.length > MAX_PENDING_REPORTS) {
      pendingReports = pendingReports.slice(-MAX_PENDING_REPORTS);
    }
    
    saveToStorage();
    
    // Try to process immediately
    processPendingReports();
  }
  
  /* *
   * Process pending reports - send to backend */
  async function processPendingReports() {
    if (pendingReports.length === 0) return;
    
    const token = localStorage.getItem('ascend_access_token');
    
    // Group reports by type
    const discounts = pendingReports.filter(r => r.type === 'discount' && r.status === 'pending');
    const orders = pendingReports.filter(r => r.type === 'order' && r.status === 'pending');
    const transactions = pendingReports.filter(r => r.type === 'transaction' && r.status === 'pending');
    
    // Send to finance backend
    try {
      const response = await fetch(`${FINANCE_API}/reports/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          discounts: discounts,
          orders: orders,
          transactions: transactions,
          platform: 'website',
          timestamp: Date.now()
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Mark successful reports
        const successIds = result.processedIds || [];
        pendingReports = pendingReports.map(r => {
          if (successIds.includes(r.id)) {
            return { ...r, status: 'synced' };
          }
          return r;
        });
        
        // Remove synced reports
        pendingReports = pendingReports.filter(r => r.status !== 'synced');
        saveToStorage();
        
        console.log('[CreditSystem] Reports synced:', successIds.length);
      }
    } catch (error) {
      console.warn('[CreditSystem] Report sync failed (will retry):', error.message);
    }
  }
  
  /* *
   * Report transaction to backend */
  function reportTransaction(transaction) {
    const report = {
      ...transaction,
      type: 'transaction',
      status: 'pending'
    };
    queueReport(report);
  }
  
  // ==========================================================================
  // CHAT APP INTEGRATION
  // ==========================================================================
  
  /* *
   * Sync with chat app backend */
  async function syncWithChatApp() {
    const identity = window.AscendIdentity?.getCurrentIdentity();
    if (!identity || !identity.isAuthenticated) return null;
    
    const token = localStorage.getItem('ascend_access_token');
    if (!token) return null;
    
    try {
      const response = await fetch(`${API_BASE}/credits/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          localBalance: credits.balance,
          localLifetime: credits.lifetimeEarned,
          lastSync: credits.lastSync,
          platform: 'website',
          timestamp: Date.now()
        })
      });
      
      if (response.ok) {
        const serverData = await response.json();
        
        // Merge with server data (server is source of truth)
        if (serverData.credits) {
          credits.balance = serverData.credits.balance;
          credits.pendingCredits = serverData.credits.pending || 0;
          credits.lifetimeEarned = serverData.credits.lifetimeEarned;
          credits.lifetimeSpent = serverData.credits.lifetimeSpent;
        }
        
        credits.lastSync = new Date().toISOString();
        saveToStorage();
        
        emitCreditChange('sync', 0, credits.balance);
        console.log('[CreditSystem] Synced with chat app, balance:', credits.balance);
        
        return serverData;
      }
    } catch (error) {
      console.warn('[CreditSystem] Chat app sync failed:', error.message);
    }
    
    return null;
  }
  
  /* *
   * Sync discount information to chat app */
  async function syncDiscountToChatApp(discountReport) {
    const token = localStorage.getItem('ascend_access_token');
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_BASE}/discounts/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          discountId: discountReport.id,
          discountType: discountReport.discountType,
          discountAmount: discountReport.discountAmount,
          categories: discountReport.categories,
          userId: discountReport.userId,
          platform: 'website',
          timestamp: Date.now()
        })
      });
      
      return response.ok;
    } catch (error) {
      console.warn('[CreditSystem] Discount sync to chat failed:', error.message);
      return false;
    }
  }
  
  /* *
   * Push credits to chat app (e.g., after purchase) */
  async function pushCreditsToChatApp() {
    const token = localStorage.getItem('ascend_access_token');
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_BASE}/credits/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          balance: credits.balance,
          pendingCredits: credits.pendingCredits,
          lastTransactions: transactions.slice(-10),
          platform: 'website',
          timestamp: Date.now()
        })
      });
      
      return response.ok;
    } catch (error) {
      console.warn('[CreditSystem] Push to chat failed:', error.message);
      return false;
    }
  }
  
  // ==========================================================================
  // SYNC MANAGEMENT
  // ==========================================================================
  
  function startSync() {
    if (syncInterval) return;
    
    // Initial sync
    syncWithChatApp();
    processPendingReports();
    
    // Periodic sync
    syncInterval = setInterval(() => {
      const identity = window.AscendIdentity?.getCurrentIdentity();
      if (identity && identity.isAuthenticated) {
        syncWithChatApp();
        processPendingReports();
      }
    }, SYNC_INTERVAL_MS);
    
    console.log('[CreditSystem] Sync started');
  }
  
  function stopSync() {
    if (syncInterval) {
      clearInterval(syncInterval);
      syncInterval = null;
      console.log('[CreditSystem] Sync stopped');
    }
  }
  
  // ==========================================================================
  // EVENTS
  // ==========================================================================
  
  function emitCreditChange(action, amount, newBalance) {
    const event = new CustomEvent('creditsChanged', {
      detail: {
        action: action,
        amount: amount,
        balance: newBalance,
        inPounds: newBalance / CREDITS_PER_POUND
      }
    });
    document.dispatchEvent(event);
  }
  
  function handleIdentityChange(event) {
    const identity = event.detail?.identity;
    
    if (identity && identity.isAuthenticated) {
      credits.userId = identity.id;
      startSync();
    } else {
      stopSync();
      // Clear credits on logout
      credits = {
        balance: 0,
        pendingCredits: 0,
        lifetimeEarned: 0,
        lifetimeSpent: 0,
        lastSync: null,
        userId: null
      };
      transactions = [];
      saveToStorage();
    }
  }
  
  // ==========================================================================
  // TRANSACTION HISTORY
  // ==========================================================================
  
  function getTransactionHistory(limit = 20) {
    return transactions.slice(-limit).reverse();
  }
  
  function getTransactionsByCategory(category) {
    return transactions.filter(t => t.category === category);
  }
  
  // ==========================================================================
  // CHECKOUT INTEGRATION
  // ==========================================================================
  
  /* *
   * Apply credits at checkout
   * Returns the credit amount that can be applied */
  function applyCreditsToCheckout(checkoutTotal, maxCreditPercent = 50) {
    // Maximum credits that can be applied (e.g., 50% of order)
    const maxCredits = poundToCredits(checkoutTotal * (maxCreditPercent / 100));
    const creditsToApply = Math.min(credits.balance, maxCredits);
    
    return {
      creditsAvailable: credits.balance,
      creditsToApply: creditsToApply,
      poundValue: creditsToPound(creditsToApply),
      remaining: credits.balance - creditsToApply,
      newTotal: checkoutTotal - creditsToPound(creditsToApply)
    };
  }
  
  /* *
   * Earn credits from purchase (reward program)
   * Default: 5% back in credits */
  function earnCreditsFromPurchase(purchaseAmount, earnPercent = 5) {
    const earnedCredits = Math.round(poundToCredits(purchaseAmount) * (earnPercent / 100));
    
    if (earnedCredits > 0) {
      addCredits(earnedCredits, 'Purchase reward', {
        category: 'reward',
        purchaseAmount: purchaseAmount,
        earnPercent: earnPercent
      });
    }
    
    return earnedCredits;
  }
  
  // ==========================================================================
  // UI HELPERS
  // ==========================================================================
  
  /* *
   * Format credits for display */
  function formatCredits(amount) {
    return amount.toLocaleString();
  }
  
  /* *
   * Get credit tier info */
  function getCreditTier() {
    const balance = credits.balance;
    
    if (balance >= 50000) return { tier: 'platinum', discount: 15, color: '#e5e4e2' };
    if (balance >= 25000) return { tier: 'gold', discount: 10, color: '#ffd700' };
    if (balance >= 10000) return { tier: 'silver', discount: 5, color: '#c0c0c0' };
    return { tier: 'bronze', discount: 0, color: '#cd7f32' };
  }
  
  // ==========================================================================
// EXPORT
// ==========================================================================

const AscendCredits = {
  init,
  getBalance,
  addCredits,
  useCredits,
  poundToCredits,
  creditsToPound,
  formatCredits,
  getCreditTier,
  getTransactionHistory,
  getTransactionsByCategory,
  applyCreditsToCheckout,
  earnCreditsFromPurchase,
  reportDiscount,
  reportOrder,
  syncWithChatApp,
  pushCreditsToChatApp,
  startSync,
  stopSync,
  CREDITS_PER_POUND
};

// Auto-init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for ES modules
export default AscendCredits;

// Also export individual functions
export { init, getBalance, addCredits, useCredits, poundToCredits, creditsToPound, formatCredits, getCreditTier, getTransactionHistory, reportDiscount, reportOrder, syncWithChatApp, CREDITS_PER_POUND };

// Also keep global for backward compatibility (in non-module scripts)
if (typeof window !== 'undefined') {
  window.AscendCredits = AscendCredits;
}
